// ============================================
// Bloom Post-Process Effect - Progressive Mip Bloom
// ============================================
// Based on: "Next Generation Post Processing in Call of Duty: Advanced Warfare"
// Uses pyramid downsample/upsample with high-quality filters

import type { IRenderContext } from "../../core/IRenderContext";
import { OffscreenTarget } from "../../core/RenderPass";
import {
  PostProcessEffect,
  type PostProcessContext,
  type PostProcessParameter,
} from "../PostProcessStack";
import {
  BLOOM_THRESHOLD_LAYOUT,
  BLOOM_DOWNSAMPLE_LAYOUT,
  BLOOM_UPSAMPLE_LAYOUT,
  BLOOM_COMPOSITE_LAYOUT,
} from "../PostProcessLayouts";
import {
  type PipelineHandle,
  type ShaderHandle,
  NULL_HANDLE,
} from "../../types";
import { getCompiledShader } from "../ShaderCache";

/**
 * Maximum number of mip levels for bloom pyramid
 */
const MAX_MIP_LEVELS = 8;

/**
 * Quality preset for bloom effect
 */
export enum BloomQuality {
  Low = 0, // 4 mip levels
  Medium = 1, // 5 mip levels
  High = 2, // 6 mip levels
  Ultra = 3, // 8 mip levels
}

const QUALITY_MIP_COUNTS: Record<BloomQuality, number> = {
  [BloomQuality.Low]: 4,
  [BloomQuality.Medium]: 5,
  [BloomQuality.High]: 6,
  [BloomQuality.Ultra]: 8,
};

/**
 * Progressive Mip Bloom post-processing effect
 *
 * Creates high-quality glow using:
 * 1. Soft threshold extraction with Karis average (anti-firefly)
 * 2. Downsample pyramid (13-tap filter with anamorphic support)
 * 3. Upsample pyramid (bicubic + tent filter with energy conservation)
 * 4. Final composite with multiple blend modes
 */
export class BloomEffect extends PostProcessEffect {
  readonly type = "bloom";
  readonly displayName = "Bloom";

  // Core parameters
  threshold = 0.9;        // Brightness threshold for bloom
  softKnee = 0.5;         // Soft knee for smooth falloff (0-1)
  intensity = 1.0;        // Overall bloom intensity
  scatter = 0.7;          // How much bloom spreads (affects upsample blend)
  
  // Advanced parameters
  anamorphic = 0.0;       // Anamorphic stretch: -1=vertical, 0=normal, 1=horizontal
  saturation = 1.0;       // Bloom color saturation (0-2)
  chromaticShift = 0.0;   // Chromatic aberration on bloom (0-1)
  dirtIntensity = 0.0;    // Lens dirt intensity (0 = disabled)
  clampMax = 10.0;        // Maximum bloom brightness (soft clamp)
  blendMode = 0;          // 0=additive, 1=screen, 2=soft light
  
  // Tint
  tintR = 1.0;
  tintG = 1.0;
  tintB = 1.0;
  quality = BloomQuality.High;

  // GPU resources
  private thresholdShader: ShaderHandle = NULL_HANDLE;
  private downsampleShader: ShaderHandle = NULL_HANDLE;
  private upsampleShader: ShaderHandle = NULL_HANDLE;
  private compositeShader: ShaderHandle = NULL_HANDLE;

  private thresholdPipeline: PipelineHandle = NULL_HANDLE;
  private downsamplePipeline: PipelineHandle = NULL_HANDLE;
  private upsamplePipeline: PipelineHandle = NULL_HANDLE;
  private compositePipelineHDR: PipelineHandle = NULL_HANDLE;
  private compositePipelineLDR: PipelineHandle = NULL_HANDLE;

  // Mip chain targets (downsampled textures)
  private mipChain: OffscreenTarget[] = [];
  // Upsample targets (separate from mip chain for blending)
  private upsampleTargets: OffscreenTarget[] = [];

  // Uniform writers
  private thresholdWriter = BLOOM_THRESHOLD_LAYOUT.createWriter();
  private downsampleWriter = BLOOM_DOWNSAMPLE_LAYOUT.createWriter();
  private upsampleWriter = BLOOM_UPSAMPLE_LAYOUT.createWriter();
  private compositeWriter = BLOOM_COMPOSITE_LAYOUT.createWriter();

  // Last size the mip chain was built for — lets a quality change rebuild the chain lazily.
  private targetWidth = 0;
  private targetHeight = 0;

  getParameters(): PostProcessParameter[] {
    return [
      {
        name: "threshold",
        type: "float",
        min: 0,
        max: 3,
        default: 0.9,
        description: "Brightness threshold",
      },
      {
        name: "softKnee",
        type: "float",
        min: 0,
        max: 1,
        default: 0.5,
        description: "Soft knee size",
      },
      {
        name: "intensity",
        type: "float",
        min: 0,
        max: 5,
        default: 1.0,
        description: "Bloom intensity",
      },
      {
        name: "scatter",
        type: "float",
        min: 0,
        max: 2,
        default: 0.7,
        description: "Bloom spread",
      },
      {
        name: "anamorphic",
        type: "float",
        min: -1,
        max: 1,
        default: 0.0,
        description: "Anamorphic stretch (-1=vert, 1=horiz)",
      },
      {
        name: "saturation",
        type: "float",
        min: 0,
        max: 2,
        default: 1.0,
        description: "Bloom saturation",
      },
      {
        name: "chromaticShift",
        type: "float",
        min: 0,
        max: 1,
        default: 0.0,
        description: "Chromatic aberration",
      },
      {
        name: "dirtIntensity",
        type: "float",
        min: 0,
        max: 2,
        default: 0.0,
        description: "Lens dirt intensity",
      },
      {
        name: "clampMax",
        type: "float",
        min: 1,
        max: 100,
        default: 10.0,
        description: "Max bloom brightness",
      },
      {
        name: "blendMode",
        type: "int",
        min: 0,
        max: 2,
        default: 0,
        description: "Blend (0=add, 1=screen, 2=soft)",
      },
      {
        name: "tintR",
        type: "float",
        min: 0,
        max: 2,
        default: 1.0,
        description: "Tint red",
      },
      {
        name: "tintG",
        type: "float",
        min: 0,
        max: 2,
        default: 1.0,
        description: "Tint green",
      },
      {
        name: "tintB",
        type: "float",
        min: 0,
        max: 2,
        default: 1.0,
        description: "Tint blue",
      },
      {
        name: "quality",
        type: "int",
        min: 0,
        max: 3,
        default: 2,
        description: "Quality (0=Low, 1=Medium, 2=High, 3=Ultra)",
      },
    ];
  }

  getParameterValue(name: string): number | boolean | number[] {
    switch (name) {
      case "threshold":
        return this.threshold;
      case "softKnee":
        return this.softKnee;
      case "intensity":
        return this.intensity;
      case "scatter":
        return this.scatter;
      case "anamorphic":
        return this.anamorphic;
      case "saturation":
        return this.saturation;
      case "chromaticShift":
        return this.chromaticShift;
      case "dirtIntensity":
        return this.dirtIntensity;
      case "clampMax":
        return this.clampMax;
      case "blendMode":
        return this.blendMode;
      case "tintR":
        return this.tintR;
      case "tintG":
        return this.tintG;
      case "tintB":
        return this.tintB;
      case "quality":
        return this.quality;
      default:
        return 0;
    }
  }

  setParameterValue(name: string, value: number | boolean | number[]): void {
    if (typeof value !== "number") return;
    switch (name) {
      case "threshold":
        this.threshold = value;
        break;
      case "softKnee":
        this.softKnee = value;
        break;
      case "intensity":
        this.intensity = value;
        break;
      case "scatter":
        this.scatter = Math.max(0, Math.min(2, value));
        break;
      case "anamorphic":
        this.anamorphic = Math.max(-1, Math.min(1, value));
        break;
      case "saturation":
        this.saturation = Math.max(0, Math.min(2, value));
        break;
      case "chromaticShift":
        this.chromaticShift = Math.max(0, Math.min(1, value));
        break;
      case "dirtIntensity":
        this.dirtIntensity = Math.max(0, value);
        break;
      case "clampMax":
        this.clampMax = Math.max(1, value);
        break;
      case "blendMode":
        this.blendMode = Math.floor(Math.max(0, Math.min(2, value)));
        break;
      case "tintR":
        this.tintR = value;
        break;
      case "tintG":
        this.tintG = value;
        break;
      case "tintB":
        this.tintB = value;
        break;
      case "quality": {
        const next = Math.floor(Math.max(0, Math.min(3, value))) as BloomQuality;
        if (next !== this.quality) {
          this.quality = next;
          // The mip chain depth depends on quality — drop it so the next render rebuilds it.
          this.destroyTargets();
        }
        break;
      }
    }
  }

  protected onInitialize(ctx: IRenderContext): void {
    const hdrFormat = 'rgba16float' as const;
    const ldrFormat = ctx.getSwapChainFormat();

    try {
      const bloomThresholdWgsl = getCompiledShader("postprocess/bloom-threshold");
      this.thresholdShader = ctx.createShader(
        bloomThresholdWgsl,
        bloomThresholdWgsl,
      );
      const bloomDownsampleWgsl = getCompiledShader("postprocess/bloom-downsample");
      this.downsampleShader = ctx.createShader(
        bloomDownsampleWgsl,
        bloomDownsampleWgsl,
      );
      const bloomUpsampleWgsl = getCompiledShader("postprocess/bloom-upsample");
      this.upsampleShader = ctx.createShader(
        bloomUpsampleWgsl,
        bloomUpsampleWgsl,
      );
      const bloomCompositeWgsl = getCompiledShader("postprocess/bloom-composite");
      this.compositeShader = ctx.createShader(
        bloomCompositeWgsl,
        bloomCompositeWgsl,
      );

      // Create pipelines
      this.thresholdPipeline = ctx.createPipeline({
        shader: this.thresholdShader,
        vertexBuffers: [],
        colorTargets: [{ format: hdrFormat }],
        primitive: { topology: "triangle-list", cullMode: "none", usesTextures: true, textureCount: 1 },
        label: "bloom-threshold",
      });

      this.downsamplePipeline = ctx.createPipeline({
        shader: this.downsampleShader,
        vertexBuffers: [],
        colorTargets: [{ format: hdrFormat }],
        primitive: { topology: "triangle-list", cullMode: "none", usesTextures: true, textureCount: 1 },
        label: "bloom-downsample",
      });

      this.upsamplePipeline = ctx.createPipeline({
        shader: this.upsampleShader,
        vertexBuffers: [],
        colorTargets: [{ format: hdrFormat }],
        primitive: {
          topology: "triangle-list",
          cullMode: "none",
          usesTextures: true,
          textureCount: 2,
        },
        label: "bloom-upsample",
      });

      this.compositePipelineHDR = ctx.createPipeline({
        shader: this.compositeShader,
        vertexBuffers: [],
        colorTargets: [{ format: hdrFormat }],
        primitive: {
          topology: "triangle-list",
          cullMode: "none",
          usesTextures: true,
          textureCount: 2,
        },
        label: "bloom-composite-hdr",
      });

      this.compositePipelineLDR = ctx.createPipeline({
        shader: this.compositeShader,
        vertexBuffers: [],
        colorTargets: [{ format: ldrFormat }],
        primitive: {
          topology: "triangle-list",
          cullMode: "none",
          usesTextures: true,
          textureCount: 2,
        },
        label: "bloom-composite-ldr",
      });
    } catch (e) {
      console.error("[BloomEffect] Failed to initialize:", e);
    }
  }

  protected onResize(ctx: IRenderContext, width: number, height: number): void {
    // Cleanup old targets
    this.destroyTargets();
    this.targetWidth = width;
    this.targetHeight = height;

    const mipCount = QUALITY_MIP_COUNTS[this.quality];

    // Create mip chain (halved resolution at each level)
    let w = Math.floor(width / 2);
    let h = Math.floor(height / 2);

    for (let i = 0; i < mipCount && w >= 2 && h >= 2; i++) {
      this.mipChain.push(new OffscreenTarget(ctx, w, h, false, true));
      w = Math.floor(w / 2);
      h = Math.floor(h / 2);
    }

    // Upsample accumulators mirror the chain minus the deepest level — the progressive upsample
    // reads the deepest downsample directly (no copy pass), so it never writes an accumulator there.
    for (let i = 0; i < this.mipChain.length - 1; i++) {
      this.upsampleTargets.push(
        new OffscreenTarget(ctx, this.mipChain[i].width, this.mipChain[i].height, false, true),
      );
    }
  }

  private destroyTargets(): void {
    for (const target of this.mipChain) {
      target.destroy();
    }
    for (const target of this.upsampleTargets) {
      target.destroy();
    }
    this.mipChain = [];
    this.upsampleTargets = [];
  }

  protected onRender(context: PostProcessContext): void {
    const { ctx, source, destination, width, height, intensity, isHdrTarget } =
      context;

    if (
      this.thresholdPipeline === NULL_HANDLE ||
      this.downsamplePipeline === NULL_HANDLE ||
      this.upsamplePipeline === NULL_HANDLE ||
      this.compositePipelineHDR === NULL_HANDLE ||
      this.compositePipelineLDR === NULL_HANDLE
    ) {
      console.error("[BloomEffect] Pipelines not initialized! Handles:", {
        threshold: this.thresholdPipeline,
        downsample: this.downsamplePipeline,
        upsample: this.upsamplePipeline,
        compositeHDR: this.compositePipelineHDR,
        compositeLDR: this.compositePipelineLDR,
        NULL_HANDLE,
      });
      return;
    }

    // Ensure targets exist (initial lazy build, or rebuild after a quality change / resize)
    if (this.mipChain.length === 0 || this.targetWidth !== width || this.targetHeight !== height) {
      this.onResize(ctx, width, height);
    }

    if (this.mipChain.length === 0) {
      console.error("[BloomEffect] No mip targets created!");
      return; // Cannot proceed without targets
    }

    const mipCount = this.mipChain.length;

    // NOTE: every pass below goes through fullscreenPass(), which snapshots the 'packed' uniforms
    // into a per-draw pool buffer. All these draws land in ONE queue submit, so without snapshots
    // they'd all read the values of the LAST setUniform (the composite pass).

    // ========================================
    // Pass 1: Threshold extraction + first downsample
    // ========================================
    const mip0 = this.mipChain[0];
    ctx.setRenderTarget(mip0.handle);
    ctx.clear(0, 0, 0, 0);
    ctx.setViewport(0, 0, mip0.width, mip0.height);

    this.thresholdWriter.setAll({
      threshold: this.threshold,
      softThreshold: this.softKnee,
      _pad0: 0,
      _pad1: 0,
    });
    this.fullscreenPass(ctx, this.thresholdPipeline, this.thresholdWriter.float32, [source]);

    // ========================================
    // Pass 2: Downsample chain
    // ========================================
    for (let i = 1; i < mipCount; i++) {
      const srcMip = this.mipChain[i - 1];
      const dstMip = this.mipChain[i];

      ctx.setRenderTarget(dstMip.handle);
      ctx.clear(0, 0, 0, 0);
      ctx.setViewport(0, 0, dstMip.width, dstMip.height);

      this.downsampleWriter.setAll({
        texelSize: [1.0 / srcMip.width, 1.0 / srcMip.height],
        mipLevel: i,
        anamorphic: this.anamorphic,
      });
      this.fullscreenPass(ctx, this.downsamplePipeline, this.downsampleWriter.float32, [
        srcMip.getTexture(),
      ]);
    }

    // ========================================
    // Pass 3: Progressive upsample (bottom to top)
    // ========================================
    // The deepest downsample IS the starting accumulator — it's read directly as the low-res
    // input of the first upsample step. (The old dedicated "seed" pass blended the smallest mip
    // with itself, doubling the widest bloom ring's energy and costing an extra pass.)
    const lastIdx = mipCount - 1;
    const mipWeight = 0.5 + this.scatter * 0.5;

    for (let i = lastIdx - 1; i >= 0; i--) {
      const lowRes = i === lastIdx - 1 ? this.mipChain[lastIdx] : this.upsampleTargets[i + 1];
      const highResMip = this.mipChain[i]; // Corresponding downsample (larger)
      const dstTarget = this.upsampleTargets[i]; // Output accumulator

      ctx.setRenderTarget(dstTarget.handle);
      ctx.clear(0, 0, 0, 0);
      ctx.setViewport(0, 0, dstTarget.width, dstTarget.height);

      this.upsampleWriter.setAll({
        texelSize: [1.0 / lowRes.width, 1.0 / lowRes.height],
        scatter: 1.0 + this.scatter, // Filter radius multiplier
        weight: mipWeight,           // Energy conservation factor
      });
      this.fullscreenPass(ctx, this.upsamplePipeline, this.upsampleWriter.float32, [
        lowRes.getTexture(),
        highResMip.getTexture(),
      ]);
    }

    // With a single mip there is no upsample step — composite straight from the threshold mip.
    const finalBloom = mipCount > 1 ? this.upsampleTargets[0] : this.mipChain[0];

    // ========================================
    // Pass 4: Final composite
    // ========================================
    ctx.setRenderTarget(destination);
    ctx.clear(0, 0, 0, 1);
    ctx.setViewport(0, 0, width, height);

    const compositePipeline = isHdrTarget
      ? this.compositePipelineHDR
      : this.compositePipelineLDR;

    this.compositeWriter.setAll({
      // context.intensity already includes this.intensity (applied by PostProcessEffect.render) —
      // multiplying by this.intensity again squared the slider value.
      intensity: intensity,
      dirtIntensity: this.dirtIntensity,
      saturation: this.saturation,
      chromaticShift: this.chromaticShift,
      tint: [this.tintR, this.tintG, this.tintB],
      clampMax: this.clampMax,
      bloomBlendMode: this.blendMode,
      _pad0: 0,
      _pad1: 0,
      _pad2: 0,
    });
    this.fullscreenPass(ctx, compositePipeline, this.compositeWriter.float32, [
      source,
      finalBloom.getTexture(),
    ]);
  }

  protected onDispose(ctx: IRenderContext): void {
    this.destroyTargets();

    // Destroy pipelines
    if (this.thresholdPipeline !== NULL_HANDLE)
      ctx.destroyPipeline(this.thresholdPipeline);
    if (this.downsamplePipeline !== NULL_HANDLE)
      ctx.destroyPipeline(this.downsamplePipeline);
    if (this.upsamplePipeline !== NULL_HANDLE)
      ctx.destroyPipeline(this.upsamplePipeline);
    if (this.compositePipelineHDR !== NULL_HANDLE)
      ctx.destroyPipeline(this.compositePipelineHDR);
    if (this.compositePipelineLDR !== NULL_HANDLE)
      ctx.destroyPipeline(this.compositePipelineLDR);

    // Destroy shaders
    if (this.thresholdShader !== NULL_HANDLE)
      ctx.destroyShader(this.thresholdShader);
    if (this.downsampleShader !== NULL_HANDLE)
      ctx.destroyShader(this.downsampleShader);
    if (this.upsampleShader !== NULL_HANDLE)
      ctx.destroyShader(this.upsampleShader);
    if (this.compositeShader !== NULL_HANDLE)
      ctx.destroyShader(this.compositeShader);

    this.thresholdPipeline = NULL_HANDLE;
    this.downsamplePipeline = NULL_HANDLE;
    this.upsamplePipeline = NULL_HANDLE;
    this.compositePipelineHDR = NULL_HANDLE;
    this.compositePipelineLDR = NULL_HANDLE;
    this.thresholdShader = NULL_HANDLE;
    this.downsampleShader = NULL_HANDLE;
    this.upsampleShader = NULL_HANDLE;
    this.compositeShader = NULL_HANDLE;
  }
}
